import { createI18n } from 'vue-i18n';
import { detectLocale } from './detectLocale';
export { I18nT, useI18n } from 'vue-i18n';
export type { ComposerTranslation } from 'vue-i18n';

export type LocaleMessages = {
  [key: string]: string | LocaleMessages;
};
type JsonModule = {
  default: LocaleMessages;
};
type LocaleModuleRecord = Record<string, JsonModule>;
type MessagesType = Record<string, LocaleMessages>;

const localesModules = import.meta.glob('./locales/*.json', { eager: true }) as LocaleModuleRecord;

export interface Language {
  code: string;
  name: string;
}

export const availableLanguages: Language[] = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
  { code: 'de', name: 'Deutsch' },
  { code: 'pt', name: 'Português' },
  { code: 'nl', name: 'Nederlands' },
  { code: 'sv', name: 'Svenska' },
  { code: 'nb', name: 'Norsk' },
  { code: 'fi', name: 'Suomi' },
  { code: 'da', name: 'Dansk' },
  { code: 'it', name: 'Italiano' },
  { code: 'pl', name: 'Polski' },
];

/**
 * Codes for the locales we ship, derived once from `availableLanguages`.
 */
const SHIPPED_CODES: readonly string[] = availableLanguages.map((l) => l.code);

const messages: MessagesType = {};
for (const pathKey in localesModules) {
  const file = pathKey.split(/[/\\]/).pop() ?? '';
  const locale = file.replace(/\.json$/i, '');
  if (locale) {
    messages[locale] = {
      ...(messages[locale] || {}),
      ...localesModules[pathKey].default,
    };
  }
}

/**
 * Resolve the user's system / browser language against the locales we ship.
 * Returns `'en'` if nothing matches or `navigator` is unavailable (SSR/tests).
 */
export const resolveSystemLocale = (): string => {
  if (typeof navigator === 'undefined') return 'en';
  const candidates: (string | undefined)[] = [...(navigator.languages ?? []), navigator.language];
  return detectLocale(candidates, SHIPPED_CODES) ?? 'en';
};

const getInitialLocale = () => resolveSystemLocale();

export const setLocale = (code: string) => {
  i18n.global.locale.value = code;
};

const i18n = createI18n({
  legacy: false,
  locale: getInitialLocale(),
  fallbackLocale: 'en',
  warnHtmlMessage: false,
  // Runtime messages are merged from JSON; vue-i18n's strict generic expects a static shape.
  messages: messages as never,
});

export default i18n;
